import type { CSSProperties } from 'react';
import { format, isSameYear } from 'date-fns';
import { ChevronLeft, ChevronRight, Globe, Link2 } from 'lucide-react';
import { cn } from '../../utils/cn';
import type { CalendarEvent } from '../../types/calendar';
import type { EventSpan } from '../../utils/calendarLayout';
import { CALENDAR_LINK_SOURCE_LABELS, type CalendarLinkSummary } from '../../services/calendarLinkSources';
import { tint } from './eventColors';

export interface EventBarProps {
  span: EventSpan;
  /** Resolved tag colour. */
  color: string;
  /** Set when a team module links this event; the bar is drawn as an outline. */
  linked?: CalendarLinkSummary;
  /** The event started in an earlier row: flat left edge. */
  continuesBefore?: boolean;
  /** The event carries on into a later row: flat right edge. */
  continuesAfter?: boolean;
  /** Prefix timed chips with their start time. */
  showTime?: boolean;
  /** Trailing detail (a time range, "Day 2 of 5"), right-aligned. */
  meta?: string;
  /** `sm` fits a month cell lane; `md` is for lists. */
  size?: 'sm' | 'md';
  onClick?: (event: CalendarEvent) => void;
  className?: string;
  style?: CSSProperties;
}

const dayLabel = (date: Date, withYear: boolean) => format(date, withYear ? 'MMM d, yyyy' : 'MMM d');

/** "Mar 10 – Mar 13", "Mar 10 · All day" or "Mar 10 · 09:00 – 10:30". */
export function describeSpan(span: EventSpan): string {
  const withYear = !isSameYear(span.start, new Date());
  if (span.shape === 'timed') {
    return `${dayLabel(span.start, withYear)} · ${format(span.start, 'HH:mm')} – ${format(span.displayEnd, 'HH:mm')}`;
  }
  if (span.dayCount === 1) return `${dayLabel(span.firstDay, withYear)} · All day`;
  return `${dayLabel(span.firstDay, withYear)} – ${dayLabel(span.lastDay, withYear)}`;
}

/** "Linked from Event Team: Build comp; Build Team: Build project". */
export function describeLinks(linked: CalendarLinkSummary | undefined): string | null {
  if (!linked || linked.targets.length === 0) return null;
  const parts = linked.targets.map(target => `${CALENDAR_LINK_SOURCE_LABELS[target.source]}: ${target.label}`);
  return `Linked from ${parts.join('; ')}`;
}

const STATUS_LABEL: Record<string, string> = { cancelled: 'Cancelled', postponed: 'Postponed' };

/**
 * One event in a calendar row.
 *
 * Three looks, so the grid reads at a glance:
 * - block (all-day or multi-day): a tinted bar with a solid left edge, drawn
 *   once across every day it covers;
 * - timed: a quiet chip with a coloured dot and the start time;
 * - linked from a team module: a dashed outline with muted text, whatever
 *   its shape, so it sits behind the calendar's own events.
 */
export const EventBar = ({
  span,
  color,
  linked,
  continuesBefore = false,
  continuesAfter = false,
  showTime = true,
  meta,
  size = 'sm',
  onClick,
  className,
  style,
}: EventBarProps) => {
  const { event, shape } = span;
  const isLinked = !!linked && linked.targets.length > 0;
  const isBlock = shape === 'block';
  const cancelled = event.status === 'cancelled';
  const postponed = event.status === 'postponed';

  // Tints go through custom properties so hover can deepen them in both themes.
  const surface = (
    isLinked
      ? { borderColor: tint(color, 0.55) }
      : isBlock
        ? {
            '--bar-bg': tint(color, 0.18),
            '--bar-bg-hover': tint(color, 0.3),
            boxShadow: continuesBefore ? undefined : `inset 3px 0 0 ${color}`,
          }
        : {}
  ) as CSSProperties;

  const tooltip = [
    event.title,
    describeSpan(span),
    STATUS_LABEL[event.status],
    event.publish ? 'Published' : null,
    describeLinks(linked),
  ]
    .filter(Boolean)
    .join(' · ');

  const trailing = event.publish || continuesAfter;

  return (
    <button
      type="button"
      onClick={() => onClick?.(event)}
      title={tooltip}
      className={cn(
        'flex items-center gap-1.5 min-w-0 text-left rounded-md transition-colors',
        size === 'sm' ? 'h-[22px] px-1.5 text-[11px]' : 'h-8 px-2.5 text-xs',
        isLinked
          ? 'border border-dashed bg-transparent hover:bg-glass-200/10'
          : isBlock
            ? 'bg-[color:var(--bar-bg)] hover:bg-[color:var(--bar-bg-hover)]'
            : 'hover:bg-glass-200/10',
        continuesBefore && 'rounded-l-none pl-1',
        continuesAfter && 'rounded-r-none pr-1',
        (cancelled || postponed) && 'opacity-60',
        className,
      )}
      style={{ ...surface, ...style }}
    >
      {continuesBefore && <ChevronLeft className="w-3 h-3 shrink-0 opacity-60 -ml-0.5" aria-hidden />}
      {!isBlock && (
        <span
          className="w-2 h-2 rounded-full shrink-0"
          style={isLinked ? { boxShadow: `inset 0 0 0 1.5px ${color}` } : { backgroundColor: color }}
          aria-hidden
        />
      )}
      {isLinked && <Link2 className="w-3 h-3 shrink-0 text-glass-400" aria-hidden />}
      {!isBlock && showTime && (
        <span className="shrink-0 tabular-nums text-glass-400">{format(span.start, 'HH:mm')}</span>
      )}
      <span
        className={cn(
          'truncate min-w-0',
          isLinked ? 'text-glass-400' : 'text-white',
          isBlock && !isLinked && 'font-medium',
          cancelled && 'line-through',
          postponed && 'italic',
        )}
      >
        {event.title}
      </span>
      {meta && <span className="ml-auto shrink-0 tabular-nums text-glass-500">{meta}</span>}
      {trailing && (
        <span className={cn('flex items-center gap-1 shrink-0', !meta && 'ml-auto')}>
          {event.publish && <Globe className="w-3 h-3 text-glass-500" aria-label="Published" />}
          {continuesAfter && <ChevronRight className="w-3 h-3 opacity-60 -mr-0.5" aria-hidden />}
        </span>
      )}
    </button>
  );
};
